export const heroTrustIconTokens = [
  "award",
  "badgeCheck",
  "building",
  "calendar",
  "check",
  "home",
  "house",
  "houseCheck",
  "leaf",
  "map",
  "minus",
  "phone",
  "shield",
  "shieldCheck",
  "tag",
  "truck",
  "wrench",
] as const;

export type HeroTrustIconToken = (typeof heroTrustIconTokens)[number];

export const heroTrustTokens = {
  colors: {
    background: "#f3eee6",
    panel: "#111411",
    text: "#ffffff",
    textSoft: "rgba(255, 255, 255, 0.78)",
    textMuted: "rgba(255, 255, 255, 0.62)",
    accent: "#ed1c24",
    accentDark: "#c90f18",
    accentSoft: "rgba(237, 28, 36, 0.14)",
    accentContrast: "#ffffff",
    darkSurface: "rgba(32, 33, 29, 0.78)",
    darkSurfaceStrong: "rgba(20, 22, 19, 0.86)",
    darkBorder: "rgba(255, 255, 255, 0.24)",
    lightSurface: "#f7f1e9",
    lightText: "#20201d",
    lightTextSoft: "#4f4a43",
    lightBorder: "rgba(55, 45, 34, 0.16)",
    proofSurface: "#f6f0e8",
    proofText: "#25231f",
    proofTextSoft: "#4d4942",
    proofDivider: "rgba(72, 59, 42, 0.18)",
    iconRing: "rgba(255, 255, 255, 0.24)",
    lightIconRing: "rgba(77, 64, 45, 0.2)",
    shadow: "0 22px 70px rgba(21, 18, 13, 0.14)",
  },
  icons: {
    eyebrow: "minus",
    check: "check",
    complete: "shieldCheck",
    primaryAction: "phone",
    secondaryAction: "calendar",
  },
  radius: {
    shell: 8,
    panel: 8,
    card: 8,
    control: 8,
    circle: 999,
  },
  sizes: {
    maxWidth: 1440,
    desktopMinHeight: 860,
  },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    eyebrow: {
      fontSize: 14,
      tabletFontSize: 12,
      mobileFontSize: 10,
      lineHeight: 1.15,
      fontWeight: 700,
      letterSpacing: 0,
    },
    title: {
      fontSize: 58,
      tabletFontSize: 45,
      mobileFontSize: 38,
      lineHeight: 1.12,
      fontWeight: 900,
      letterSpacing: 0,
    },
    lead: {
      fontSize: 20,
      tabletFontSize: 17,
      mobileFontSize: 20,
      lineHeight: 1.48,
      fontWeight: 500,
      letterSpacing: 0,
    },
    featureTitle: {
      fontSize: 17,
      mobileFontSize: 20,
      lineHeight: 1.22,
      fontWeight: 850,
      letterSpacing: 0,
    },
    featureText: {
      fontSize: 14,
      mobileFontSize: 18,
      lineHeight: 1.45,
      fontWeight: 500,
      letterSpacing: 0,
    },
    action: {
      fontSize: 22,
      mobileFontSize: 21,
      lineHeight: 1.12,
      fontWeight: 850,
      letterSpacing: 0,
    },
    actionDescription: {
      fontSize: 13,
      mobileFontSize: 16,
      lineHeight: 1.28,
      fontWeight: 600,
      letterSpacing: 0,
    },
    proof: {
      fontSize: 13,
      mobileFontSize: 12,
      lineHeight: 1.22,
      fontWeight: 850,
      letterSpacing: 0,
    },
  },
  defaults: {
    eyebrow: "Sydney's Trusted Pest Control Experts",
    title: "Pest Control You Can Trust, Sydney Homes & Businesses Rely On.",
    titleHighlights: ["Trust,", "Rely On."],
    lead:
      "Local, licensed and committed to exceptional results. Real technicians, real care, real peace of mind.",
    completeTitle: "Complete Pest Control Solutions",
    completeText: "For every property, every pest.",
    reassuranceTitle: "Safe. Licensed. Reliable.",
    reassuranceText: "Trusted care for your family, pets and property.",
    supportNote: {
      title: "Speak with a real local technician",
      text: "No call centres. Just expert advice.",
      icon: "phone",
    },
    primaryAction: {
      label: "1300 661 886",
      href: "tel:1300661886",
      mobileLabel: "Call Us 1300 661 886",
      description: "Speak with a real local technician",
      icon: "phone",
    },
    secondaryAction: {
      label: "Book Online",
      href: "#book-online",
      description: "It's fast & easy",
      icon: "calendar",
    },
    primaryFeatures: [
      {
        icon: "home",
        title: "Home Pest Control",
        text: "Cockroaches, spiders, rodents & more",
      },
      {
        icon: "building",
        title: "Commercial Pest Control",
        text: "Strata, restaurants, warehouses & more",
      },
      {
        icon: "wrench",
        title: "Pre & Post Construction",
        text: "Protection before you build, after you build",
      },
      {
        icon: "shieldCheck",
        title: "100% Satisfaction Guarantee",
        text: "We're not happy until you are",
      },
    ],
    secondaryFeatures: [
      {
        icon: "tag",
        title: "Pest Control Packages From $99",
        text: "Affordable solutions for every property",
      },
      {
        icon: "leaf",
        title: "Eco-Friendly, Pet & Child Safe",
        text: "Tough on pests, gentle on what matters",
      },
      {
        icon: "shieldCheck",
        title: "Fully Licensed & Insured",
        text: "Qualified professionals you can rely on",
      },
      {
        icon: "truck",
        title: "Fast Local Same Day Service",
        text: "We're nearby and ready to help",
      },
      {
        icon: "houseCheck",
        title: "End Of Lease Pest Control & Cleaning",
        text: "Move out with confidence",
      },
    ],
    proofItems: [
      {
        icon: "map",
        title: "Australian Owned & Operated",
      },
      {
        icon: "leaf",
        title: "Eco Friendly",
      },
      {
        icon: "shieldCheck",
        title: "Fully Licensed & Insured",
      },
      {
        icon: "award",
        title: "100% Satisfaction Guarantee",
      },
    ],
  },
} as const;

export type HeroTrustColorToken = keyof typeof heroTrustTokens.colors;
export type HeroTrustIconSlot = keyof typeof heroTrustTokens.icons;
